import { useCallback, useState } from 'react';
import { GrcNotify, NotifyType } from '@grc/ui-package';
import { usePutApiData } from '@grc/ui-utility';
import { useI18nState } from '../../stores/settings/language-context';
import {
  SubmitQuoteApproval,
  UpdateQuoteStatus
} from '../../service/api/get-quotes-service';
import {
  ApprovalStatus,
  QuoteStatus
} from '../../models/quotes/quote.interface';
import { ApprovalDecisionType } from './approval-decision-popup/approval-decision-popup.interface';

export type ApprovalDecisionSource = 'quoteRequests' | 'pendingForApproval';

const CURRENT_APPROVER_ID_PLACEHOLDER = '8ae34d04-c0d4-47b1-938c-2e991b35eb5e';

interface IApprovalDecisionState {
  visible: boolean;
  decisionType: ApprovalDecisionType;
  quoteIds: string[];
  source: ApprovalDecisionSource;
  isSubmitting: boolean;
}

const initialState: IApprovalDecisionState = {
  visible: false,
  decisionType: 'approve',
  quoteIds: [],
  source: 'pendingForApproval',
  isSubmitting: false
};

export function useApprovalDecision(onComplete: () => void) {
  const { putData } = usePutApiData();
  const { localeData } = useI18nState();
  const [state, setState] = useState<IApprovalDecisionState>(initialState);

  const openApprove = useCallback(
    (quoteIds: string[], source: ApprovalDecisionSource) => {
      setState({
        visible: true,
        decisionType: 'approve',
        quoteIds,
        source,
        isSubmitting: false
      });
    },
    []
  );

  const openReject = useCallback(
    (quoteIds: string[], source: ApprovalDecisionSource) => {
      setState({
        visible: true,
        decisionType: 'reject',
        quoteIds,
        source,
        isSubmitting: false
      });
    },
    []
  );

  const closePopup = useCallback(() => {
    setState(prev => ({ ...prev, visible: false }));
  }, []);

  const submit = useCallback(
    async (comments: string) => {
      setState(prev => ({ ...prev, isSubmitting: true }));

      const submitOne = (quoteId: string) =>
        state.source === 'quoteRequests'
          ? UpdateQuoteStatus(putData, quoteId, QuoteStatus.Cancelled, comments)
          : SubmitQuoteApproval(putData, quoteId, {
              approverId: CURRENT_APPROVER_ID_PLACEHOLDER,
              status:
                state.decisionType === 'approve'
                  ? ApprovalStatus.Approved
                  : ApprovalStatus.Rejected,
              reason: comments
            });

      const results = await Promise.all(state.quoteIds.map(submitOne));
      const successCount = results.filter(Boolean).length;
      const failedCount = results.length - successCount;

      if (failedCount === 0) {
        GrcNotify({
          message: localeData.QuoteDecisionSubmittedSuccessfully,
          status: NotifyType.Success
        });
      } else if (successCount > 0) {
        GrcNotify({
          message: localeData.QuoteDecisionPartialFailure.replace(
            '{success}',
            String(successCount)
          )
            .replace('{total}', String(results.length))
            .replace('{failed}', String(failedCount)),
          status: NotifyType.Warning
        });
      } else {
        GrcNotify({
          message: localeData.QuoteDecisionFailed,
          status: NotifyType.Error
        });
      }

      setState(prev => ({ ...prev, visible: false, isSubmitting: false }));
      onComplete();
    },
    [
      state.quoteIds,
      state.source,
      state.decisionType,
      putData,
      localeData,
      onComplete
    ]
  );

  return {
    isPopupVisible: state.visible,
    decisionType: state.decisionType,
    quoteIds: state.quoteIds,
    isSubmitting: state.isSubmitting,
    openApprove,
    openReject,
    closePopup,
    submit
  };
}

export default useApprovalDecision;
